import React, { useState, useEffect } from 'react';
import { 
  MdSearch, 
  MdFileDownload, 
  MdCheckCircle, 
  MdCancel,
  MdVisibility,
  MdRefresh,
  MdImage,
  MdClose
} from 'react-icons/md';
import './KYCQueue.css';
import { getAllKYCDocuments, updateKYCStatus } from '../../../lib/supabase/helpers';
import { supabase } from '../../../lib/supabase/client';

export default function KYCQueue() {
  const [kycRequests, setKycRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selectedKyc, setSelectedKyc] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchKYCRequests();
  }, []);

  const fetchKYCRequests = async () => {
    try {
      setLoading(true);
      const data = await getAllKYCDocuments();
      
      // Group by user_id to combine all documents per user
      const groupedByUser = {};
      data.forEach(doc => {
        if (!groupedByUser[doc.user_id]) {
          groupedByUser[doc.user_id] = {
            id: doc.user_id,
            userId: doc.user_id,
            userName: `${doc.profiles?.first_name || ''} ${doc.profiles?.last_name || ''}`.trim() || 'Unknown User',
            userEmail: doc.profiles?.email || 'N/A',
            phone: doc.profiles?.phone || 'N/A',
            status: 'PENDING',
            submittedAt: new Date(doc.created_at),
            verifiedBy: doc.verified_by,
            verifiedAt: doc.verified_at ? new Date(doc.verified_at) : null,
            documents: {},
            allDocumentIds: []
          };
        }
        
        groupedByUser[doc.user_id].documents[doc.document_type] = {
          id: doc.id,
          url: doc.document_url,
          number: doc.document_number,
          status: doc.status
        };

        groupedByUser[doc.user_id].allDocumentIds.push(doc.id);

        // Set overall status based on document statuses
        const allApproved = Object.values(groupedByUser[doc.user_id].documents)
          .every(d => d.status === 'approved');
        const anyRejected = Object.values(groupedByUser[doc.user_id].documents)
          .some(d => d.status === 'rejected');

        if (anyRejected) {
          groupedByUser[doc.user_id].status = 'REJECTED';
        } else if (allApproved && Object.keys(groupedByUser[doc.user_id].documents).length >= 4) {
          groupedByUser[doc.user_id].status = 'APPROVED';
        } else {
          groupedByUser[doc.user_id].status = 'PENDING';
        }
      });

      const formattedKYC = Object.values(groupedByUser).map(user => ({
        ...user,
        panNumber: user.documents.pan?.number || 'N/A',
        panDocUrl: user.documents.pan?.url || '',
        aadhaarNumber: user.documents.aadhaar?.number || 'N/A',
        aadhaarDocUrl: user.documents.aadhaar?.url || '',
        selfieUrl: user.documents.selfie?.url || '',
        addressProofUrl: user.documents.proof_of_address?.url || '',
        address: 'As per documents'
      }));

      setKycRequests(formattedKYC);
    } catch (error) {
      console.error('Error fetching KYC requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (kycId) => {
    if (!window.confirm('Are you sure you want to approve ALL documents for this user?')) {
      return;
    }

    setProcessing(true);

    try {
      const kyc = kycRequests.find(k => k.id === kycId);
      
      if (!kyc) {
        alert('KYC request not found');
        return;
      }

      // Get current user (broker)
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        alert('You must be logged in to approve KYC');
        return;
      }

      // Approve all document types for this user
      const documentTypes = ['pan', 'aadhaar', 'selfie', 'proof_of_address'];
      const updatePromises = [];

      for (const docType of documentTypes) {
        if (kyc.documents[docType]) {
          const docId = kyc.documents[docType].id;
          updatePromises.push(
            supabase
              .from('kyc_documents')
              .update({
                status: 'approved',
                verified_by: user.id,
                verified_at: new Date().toISOString()
              })
              .eq('id', docId)
          );
        }
      }

      const results = await Promise.all(updatePromises);
      
      // Check if any updates failed
      const failed = results.filter(r => r.error);
      if (failed.length > 0) {
        console.error('Some updates failed:', failed);
        alert('Some documents failed to approve. Please try again.');
        return;
      }

      // Update user's overall KYC status in profiles table
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          kyc_status: 'approved',
          updated_at: new Date().toISOString()
        })
        .eq('id', kycId);

      if (profileError) {
        console.error('Error updating profile KYC status:', profileError);
        alert('KYC approved but failed to update profile status');
      } else {
        alert('KYC approved successfully! User can now access all trading features.');
      }

      await fetchKYCRequests();
      setShowModal(false);
    } catch (error) {
      console.error('Error approving KYC:', error);
      alert('Failed to approve KYC. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (kycId) => {
    const reason = prompt('Enter rejection reason (will be sent to user):');
    
    if (!reason || !reason.trim()) {
      return;
    }

    setProcessing(true);

    try {
      const kyc = kycRequests.find(k => k.id === kycId);
      
      if (!kyc) {
        alert('KYC request not found');
        return;
      }

      // Get current user (broker)
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        alert('You must be logged in to reject KYC');
        return;
      }

      // Reject all document types for this user
      const documentTypes = ['pan', 'aadhaar', 'selfie', 'proof_of_address'];
      const updatePromises = [];

      for (const docType of documentTypes) {
        if (kyc.documents[docType]) {
          const docId = kyc.documents[docType].id;
          updatePromises.push(
            supabase
              .from('kyc_documents')
              .update({
                status: 'rejected',
                verified_by: user.id,
                verified_at: new Date().toISOString(),
                rejection_reason: reason.trim()
              })
              .eq('id', docId)
          );
        }
      }

      const results = await Promise.all(updatePromises);
      
      // Check if any updates failed
      const failed = results.filter(r => r.error);
      if (failed.length > 0) {
        console.error('Some updates failed:', failed);
        alert('Some documents failed to reject. Please try again.');
        return;
      }

      // Update user's overall KYC status in profiles table
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          kyc_status: 'rejected',
          updated_at: new Date().toISOString()
        })
        .eq('id', kycId);

      if (profileError) {
        console.error('Error updating profile KYC status:', profileError);
        alert('KYC rejected but failed to update profile status');
      } else {
        alert('KYC rejected! User has been notified.');
      }

      await fetchKYCRequests();
      setShowModal(false);
    } catch (error) {
      console.error('Error rejecting KYC:', error);
      alert('Failed to reject KYC. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleViewDetails = (kyc) => {
    setSelectedKyc(kyc);
    setShowModal(true);
  };

  const handleViewImage = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  const handleExport = () => {
    // Create CSV content
    const headers = ['User Name', 'Email', 'Phone', 'PAN', 'Aadhaar', 'Status', 'Submitted Date'];
    const rows = filteredKyc.map(kyc => [
      kyc.userName,
      kyc.userEmail,
      kyc.phone,
      kyc.panNumber,
      kyc.aadhaarNumber,
      kyc.status,
      kyc.submittedAt.toLocaleDateString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kyc_requests_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const filteredKyc = kycRequests.filter(kyc => {
    const matchesSearch = 
      kyc.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      kyc.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      kyc.panNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      kyc.aadhaarNumber.includes(searchTerm);
    
    const matchesStatus = statusFilter === 'ALL' || kyc.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const stats = {
    pending: kycRequests.filter(k => k.status === 'PENDING').length,
    approved: kycRequests.filter(k => k.status === 'APPROVED').length,
    rejected: kycRequests.filter(k => k.status === 'REJECTED').length,
    total: kycRequests.length
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading KYC requests...</p>
      </div>
    );
  }

  return (
    <div className="kyc-container">
      {/* Stats Cards */}
      <div className="kyc-stats">
        <div className="stat-card-small pending">
          <div className="stat-icon">⏳</div>
          <div className="stat-info">
            <p>Pending Review</p>
            <h3>{stats.pending}</h3>
          </div>
        </div>
        <div className="stat-card-small approved">
          <div className="stat-icon">✅</div>
          <div className="stat-info">
            <p>Approved</p>
            <h3>{stats.approved}</h3>
          </div>
        </div>
        <div className="stat-card-small rejected">
          <div className="stat-icon">❌</div>
          <div className="stat-info">
            <p>Rejected</p>
            <h3>{stats.rejected}</h3>
          </div>
        </div>
        <div className="stat-card-small total">
          <div className="stat-icon">📋</div>
          <div className="stat-info">
            <p>Total Submissions</p>
            <h3>{stats.total}</h3>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="kyc-header">
        <div>
          <h1>KYC Verification Queue</h1>
          <p>Review and verify client KYC documents</p>
        </div>
        <button className="btn btn-primary" onClick={fetchKYCRequests} disabled={loading}>
          <MdRefresh size={20} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Filters */}
      <div className="kyc-filters">
        <div className="search-box">
          <MdSearch size={20} />
          <input
            type="text"
            placeholder="Search by name, email, PAN, Aadhaar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="ALL">All Status</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </select>

        <button className="btn btn-success" onClick={handleExport}>
          <MdFileDownload size={20} />
          <span>Export CSV</span>
        </button>
      </div>

      {/* Results Info */}
      <div className="results-info">
        <p>Showing {filteredKyc.length} of {kycRequests.length} KYC requests</p>
      </div>

      {/* KYC Table */}
      <div className="kyc-table-container">
        <table className="kyc-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Client</th>
              <th>PAN Number</th>
              <th>Aadhaar Number</th>
              <th>Documents</th>
              <th>Status</th>
              <th>Submitted</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredKyc.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-data">
                  No KYC requests found
                </td>
              </tr>
            ) : (
              filteredKyc.map((kyc) => (
                <tr key={kyc.id}>
                  <td>
                    <span className="kyc-id">#{kyc.id.substring(0, 8)}</span>
                  </td>
                  <td>
                    <div className="client-info-small">
                      <div className="client-name">{kyc.userName}</div>
                      <div className="client-email">{kyc.userEmail}</div>
                      <div className="client-phone">{kyc.phone}</div>
                    </div>
                  </td>
                  <td>
                    <span className="doc-number">{kyc.panNumber}</span>
                  </td>
                  <td>
                    <span className="doc-number">{kyc.aadhaarNumber}</span>
                  </td>
                  <td>
                    <div className="document-previews">
                      {kyc.panDocUrl && (
                        <button 
                          className="doc-preview-btn"
                          onClick={() => handleViewImage(kyc.panDocUrl)}
                          title="View PAN"
                        >
                          <MdImage size={16} />
                          PAN
                        </button>
                      )}
                      {kyc.aadhaarDocUrl && (
                        <button 
                          className="doc-preview-btn"
                          onClick={() => handleViewImage(kyc.aadhaarDocUrl)}
                          title="View Aadhaar"
                        >
                          <MdImage size={16} />
                          Aadhaar
                        </button>
                      )}
                      {kyc.selfieUrl && (
                        <button 
                          className="doc-preview-btn"
                          onClick={() => handleViewImage(kyc.selfieUrl)}
                          title="View Selfie"
                        >
                          <MdImage size={16} />
                          Selfie
                        </button>
                      )}
                      {kyc.addressProofUrl && (
                        <button 
                          className="doc-preview-btn"
                          onClick={() => handleViewImage(kyc.addressProofUrl)}
                          title="View Address Proof"
                        >
                          <MdImage size={16} />
                          Address
                        </button>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge status-${kyc.status.toLowerCase()}`}>
                      {kyc.status}
                    </span>
                  </td>
                  <td>{kyc.submittedAt.toLocaleDateString()}</td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn-icon btn-view" 
                        title="View Full Details"
                        onClick={() => handleViewDetails(kyc)}
                      >
                        <MdVisibility size={18} />
                      </button>
                      {kyc.status === 'PENDING' && (
                        <>
                          <button 
                            className="btn-icon btn-approve" 
                            title="Approve KYC"
                            onClick={() => handleApprove(kyc.id)}
                            disabled={processing}
                          >
                            <MdCheckCircle size={18} />
                          </button>
                          <button 
                            className="btn-icon btn-reject" 
                            title="Reject KYC"
                            onClick={() => handleReject(kyc.id)}
                            disabled={processing}
                          >
                            <MdCancel size={18} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination">
        <button className="btn btn-secondary" disabled>Previous</button>
        <span>Page 1 of 1</span>
        <button className="btn btn-secondary" disabled>Next</button>
      </div>

      {/* Details Modal */}
      {showModal && selectedKyc && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>KYC Verification Details</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="kyc-details-grid">
                {/* Personal Information */}
                <div className="detail-section">
                  <h3>Personal Information</h3>
                  <div className="detail-row">
                    <span className="detail-label">Name:</span>
                    <span className="detail-value">{selectedKyc.userName}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Email:</span>
                    <span className="detail-value">{selectedKyc.userEmail}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Phone:</span>
                    <span className="detail-value">{selectedKyc.phone}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Address:</span>
                    <span className="detail-value">{selectedKyc.address}</span>
                  </div>
                </div>

                {/* Document Numbers */}
                <div className="detail-section">
                  <h3>Document Numbers</h3>
                  <div className="detail-row">
                    <span className="detail-label">PAN Number:</span>
                    <span className="detail-value doc-highlight">{selectedKyc.panNumber}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Aadhaar Number:</span>
                    <span className="detail-value doc-highlight">{selectedKyc.aadhaarNumber}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Status:</span>
                    <span className={`status-badge status-${selectedKyc.status.toLowerCase()}`}>
                      {selectedKyc.status}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Submitted:</span>
                    <span className="detail-value">{selectedKyc.submittedAt.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Document Images */}
              <div className="detail-section">
                <h3>Uploaded Documents</h3>
                <div className="documents-grid">
                  {selectedKyc.panDocUrl && (
                    <div className="document-card">
                      <div className="document-label">PAN Card</div>
                      <img 
                        src={selectedKyc.panDocUrl} 
                        alt="PAN Card"
                        onClick={() => handleViewImage(selectedKyc.panDocUrl)}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                  )}
                  {selectedKyc.aadhaarDocUrl && (
                    <div className="document-card">
                      <div className="document-label">Aadhaar Card</div>
                      <img 
                        src={selectedKyc.aadhaarDocUrl} 
                        alt="Aadhaar Card"
                        onClick={() => handleViewImage(selectedKyc.aadhaarDocUrl)}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                  )}
                  {selectedKyc.selfieUrl && (
                    <div className="document-card">
                      <div className="document-label">Selfie</div>
                      <img 
                        src={selectedKyc.selfieUrl} 
                        alt="Selfie"
                        onClick={() => handleViewImage(selectedKyc.selfieUrl)}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                  )}
                  {selectedKyc.addressProofUrl && (
                    <div className="document-card">
                      <div className="document-label">Address Proof</div>
                      <img 
                        src={selectedKyc.addressProofUrl} 
                        alt="Address Proof"
                        onClick={() => handleViewImage(selectedKyc.addressProofUrl)}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {selectedKyc.status === 'APPROVED' && selectedKyc.verifiedAt && (
                <div className="alert alert-success">
                  <strong>Verified on:</strong> {selectedKyc.verifiedAt?.toLocaleString()}
                </div>
              )}
            </div>
            {selectedKyc.status === 'PENDING' && (
              <div className="modal-footer">
                <button 
                  className="btn btn-success"
                  onClick={() => handleApprove(selectedKyc.id)}
                  disabled={processing}
                >
                  <MdCheckCircle size={20} />
                  {processing ? 'Processing...' : 'Approve KYC'}
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={() => handleReject(selectedKyc.id)}
                  disabled={processing}
                >
                  <MdCancel size={20} />
                  {processing ? 'Processing...' : 'Reject KYC'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Image Viewer Modal */}
      {showImageModal && (
        <div className="modal-overlay" onClick={() => setShowImageModal(false)}>
          <div className="image-modal" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={() => setShowImageModal(false)}>
              <MdClose size={24} />
            </button>
            <img src={selectedImage} alt="Document" />
          </div>
        </div>
      )}
    </div>
  );
}